/**
 * `useCopyMoveDragStore` — transient drag session for the CCM drag-to-
 * destination affordance (handoff §1.1).
 *
 * When the lawyer starts dragging a `sources/` resource row, the row writes
 * its address here (`begin`) and the always-mounted `CopyMoveDropOverlay`
 * reveals the destinations rail. The store also tracks the live COPY MODIFIER
 * (⌥/Alt or Ctrl held) so the overlay can show the "＋ copy" badge and the
 * drop applies copy-vs-move (default = move) without re-reading the
 * `DragEvent` (which isn't available on the overlay's own render).
 *
 * Not persisted — a drag never outlives the gesture. `end` clears it (fired
 * on dragend AND after a drop, so a cancelled drag tears the overlay down).
 */

import { create } from "zustand";
import type { ResourceCopyMoveInput } from "@/hooks/useResourceCopyMove";

export interface CopyMoveDragSession {
  /** The resource being dragged, workspace-relative. */
  path: string;
  /** Display basename (toast / overlay headline). */
  name: string;
  /** The scope the resource is sourced FROM (case full triple, or org). */
  source: ResourceCopyMoveInput["source"];
}

interface CopyMoveDragStore {
  /** The active drag, or null when nothing is being dragged. */
  drag: CopyMoveDragSession | null;
  /** True while the COPY modifier (Alt/Ctrl) is held during the drag —
   *  flips the default Move to Copy. */
  copyModifier: boolean;
  begin: (session: CopyMoveDragSession) => void;
  setCopyModifier: (held: boolean) => void;
  end: () => void;
}

export const useCopyMoveDragStore = create<CopyMoveDragStore>((set) => ({
  drag: null,
  copyModifier: false,
  begin: (session) => set({ drag: session, copyModifier: false }),
  setCopyModifier: (held) => set({ copyModifier: held }),
  end: () => set({ drag: null, copyModifier: false }),
}));

/** The bespoke drag MIME for a copy/move resource drag — distinct from the
 *  composer @-mention MIME (`WORKSPACE_FILE_MIME`) so the two drop targets
 *  never cross-accept. The drop overlay only accepts THIS type. */
export const COPY_MOVE_DRAG_MIME = "application/vnd.lexiun.copy-move-source+json";
